import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar, Stack, Tab, Tabs } from '@mui/material';
import { PageHeader, EmptyState } from '../ui';
import { GatewayCard } from './GatewayCard';
import { PricingEditor } from './PricingEditor';
import { ReconcilePanel } from './ReconcilePanel';
import { SweepPanel } from './SweepPanel';
import {
  paymentService,
  type BonusTier,
  type PaymentProvider,
  type PricingRow,
} from '../../services/paymentService';

type TabKey = 'gateways' | 'pricing' | 'reconcile' | 'sweep';

/**
 * Màn hình thanh toán của admin.
 *
 * Ba việc tách nhau rõ: cấu hình CỔNG (ra tiền vào), cấu hình GIÁ (tiền ra bao
 * nhiêu), và ĐỐI SOÁT (khoản nào lạc). Gộp chung một trang vì chúng dùng chung
 * bối cảnh, tách tab vì thao tác trên chúng không bao giờ đi cùng nhau.
 */
export const PaymentSettings: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('gateways');
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [configFields, setConfigFields] = useState<Record<string, string[]>>({});
  const [pricing, setPricing] = useState<PricingRow[]>([]);
  const [tiers, setTiers] = useState<BonusTier[]>([]);
  const [rateBasisDays, setRateBasisDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const onToast = useCallback((msg: string, sev: 'success' | 'error') => setToast({ msg, sev }), []);

  const loadProviders = useCallback(async () => {
    try {
      const r = await paymentService.listProviders();
      setProviders(r.providers ?? []);
      setConfigFields(r.configFields ?? {});
    } catch (e) {
      onToast('Không tải được cổng thanh toán: ' + (e as Error).message, 'error');
    }
  }, [onToast]);

  const loadPricing = useCallback(async () => {
    try {
      const r = await paymentService.getPricing();
      setPricing(r.pricing ?? []);
      setTiers(r.tiers ?? []);
      setRateBasisDays(r.rateBasisDays ?? 30);
    } catch (e) {
      onToast('Không tải được bảng giá: ' + (e as Error).message, 'error');
    }
  }, [onToast]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      await Promise.all([loadProviders(), loadPricing()]);
      setLoading(false);
    })();
  }, [loadProviders, loadPricing]);

  const anyEnabled = providers.some((p) => p.enabled);

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        eyebrow="Thanh toán"
        title="Cấu hình thanh toán"
        description="Cổng nhận tiền, bảng giá license, bậc bonus nạp điểm, và đối soát những khoản tiền chưa khớp đơn."
      />

      {!loading && !anyEnabled && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Chưa có cổng nào được bật — trang /topup hiện không nhận được tiền. Điền Binance ID và
          push key ở tab Cổng thanh toán, bấm Kiểm tra, rồi mới bật.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v as TabKey)} sx={{ mb: 3 }}>
        <Tab value="gateways" label="Cổng thanh toán" />
        <Tab value="pricing" label="Bảng giá & bonus" />
        <Tab value="reconcile" label="Đối soát" />
        <Tab value="sweep" label="Gom tiền" />
      </Tabs>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress size={32} />
        </Stack>
      ) : tab === 'gateways' ? (
        <Stack spacing={3}>
          {providers.length === 0 ? (
            <EmptyState
              title="Chưa có cổng thanh toán nào"
              description="Bảng payment_providers rỗng — kiểm tra migration 0006_payments.sql đã chạy chưa."
            />
          ) : (
            providers.map((p) => (
              <GatewayCard
                key={p.id}
                provider={p}
                allowedKeys={configFields[p.kind] ?? []}
                onSaved={() => void loadProviders()}
                onToast={onToast}
              />
            ))
          )}
        </Stack>
      ) : tab === 'pricing' ? (
        <PricingEditor
          pricing={pricing}
          tiers={tiers}
          rateBasisDays={rateBasisDays}
          onChanged={() => void loadPricing()}
          onToast={onToast}
        />
      ) : tab === 'reconcile' ? (
        <ReconcilePanel onToast={onToast} />
      ) : (
        <SweepPanel onToast={onToast} />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.sev ?? 'success'} onClose={() => setToast(null)} variant="filled">
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};
